import { useSiteSdm } from "@inrange/building-manager-api-client";
import { trackUserInteraction } from "@inrange/building-manager-api-client/events";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import {
  getOnsitePpaLengthsConfig,
  getPpas,
  Site,
  TimedTariffAggregateEnergyFlow,
} from "@inrange/building-manager-api-client/models-site";
import {
  Column,
  ColumnNoDefaults,
  EnergyDemandSidebar,
  EnergySupplySidebar,
  Row,
  SimpleButton,
  Tooltip,
} from "@inrange/theme-components";
import {
  PeakOffPeakChart,
  YearDayEnergyChart,
} from "@inrange/theme-components/charts";
import {
  formatBatterySummary,
  formatCurrencyToNearestTen,
  formatLeaseTariffBillingFrequency,
  formatTariff,
  formatUnits,
  fractionalCurrencySymbol,
} from "@inrange/theme-components/formatting";
import {
  MarketplaceEnergyCurvesModal,
  siteCanBuy,
  siteCanSell,
} from "@inrange/theme-components/marketplace";
import React, { ReactNode, useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { UserContext } from "../../auth/UserContext";
import { formatBuyDescription } from "../../utils/sdm";
import { getProposalType } from "../../utils/utils";
import {
  genEnergyProcuredChartData,
  genPeakOffPeakChartDataYear,
} from "./chartDataFormatting";
import NetworkSitesBar from "./NetworkSitesBar";

interface EnergySavingsProps {
  organisation: Organisation;
  site: Site;
  investmentModel: string;
  isOwnerOccupier: boolean;
  isGeneratingSite: boolean;
}

const capitalise = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const EnergySavings: React.FC<EnergySavingsProps> = ({
  organisation,
  site,
  investmentModel,
  isOwnerOccupier,
  isGeneratingSite,
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlSpillSiteId = params.get("spillSiteId") || undefined;
  const { user } = useContext(UserContext);
  const { fetchSiteSdmList, bestBuyOffer } = useSiteSdm({
    siteId: site.id,
    onlyLinkedOrgs: !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE,
    userOrgId: organisation.id,
  });
  const [energyProcuredChartPeriod, setEnergyProcuredChartPeriod] = useState<
    "year" | "summer" | "winter"
  >("year");
  const [showEnergyCurvesModal, setShowEnergyCurvesModal] =
    useState<boolean>(false);

  const isBatterySite = !!site.totalBatterySummary;
  const isBatteryArbitrageSite = site.energyFlowAnnual.batteryGridCharge > 0;
  const proposalType = getProposalType(
    organisation.orgType,
    isGeneratingSite,
    isOwnerOccupier
  );

  const hasWiredBuy = site.sdmMatches.some(
    (match) => match.buyerId === site.id && match.isWired
  );
  const isNotYetProcuringSite =
    site.energyFlowAnnual.behindMeter + site.energyFlowAnnual.networkImport ===
      0 && site.energyFlowAnnual.demand > 0;

  const ppaSavings1 =
    isOwnerOccupier && site.activeInvestmentModel === "license"
      ? site.financialModels.ooLicenseProcurement.savingsPerPpa[0]
      : site.financialModels.procurement.savingsPerPpa[0];

  const ppas = getPpas(getOnsitePpaLengthsConfig(site), "other");
  let ppaTotalLength = 0;
  for (let i = 0; i < ppas.length; i++) {
    ppaTotalLength += ppas[i].ppaLength as number;
  }

  isOwnerOccupier && site.activeInvestmentModel === "license"
    ? site.financialModels.ooLicenseProcurement.savingsPerPpa.length > 1
      ? site.financialModels.ooLicenseProcurement.savingsPerPpa[1]
      : 0
    : site.financialModels.procurement.savingsPerPpa.length > 1
      ? site.financialModels.procurement.savingsPerPpa[1]
      : 0;
  const isOnsitePvLease =
    site.tariffConfig.onsitePPA.ppaPurchaseModel === "fixed_lease";
  let onsiteTariff = formatTariff(
    site.tariffConfig.onsitePPA.tariff,
    site.tariffConfig.onsitePPA.ppaPurchaseModel,
    site.tariffConfig.onsitePPA.ppaLeaseBillingFrequency,
    organisation.orgType === "Landlord",
    site.currencyCode,
    true,
    false
  );
  let onsiteTariffFreq: ReactNode | undefined = undefined;
  if (isOwnerOccupier && site.activeInvestmentModel === "license") {
    onsiteTariff = "0" + fractionalCurrencySymbol(site.currencyCode) + "/kWh";
  } else if (isOnsitePvLease) {
    onsiteTariffFreq = (
      <h5>
        {formatLeaseTariffBillingFrequency(
          organisation.orgType === "Landlord"
            ? "annually"
            : site.tariffConfig.onsitePPA.ppaLeaseBillingFrequency
        )}
      </h5>
    );
  }

  const energyProcuredChartData = genEnergyProcuredChartData(
    site,
    energyProcuredChartPeriod
  );
  const peakOffPeakChartData = genPeakOffPeakChartDataYear(site);

  const timedTariffsByTariffRate: Record<
    string,
    Record<string, TimedTariffAggregateEnergyFlow>
  > = site.energyFlowAnnual.timedImportTariffs.reduce((acc, tariff) => {
    if (!acc[tariff.tariffName]) {
      acc[tariff.tariffName] = {};
    }
    if (!acc[tariff.tariffName][tariff.rateName]) {
      acc[tariff.tariffName][tariff.rateName] = tariff;
    }
    return acc;
  }, {}) || {};

  const peakBehindMeter =
    timedTariffsByTariffRate["Peak/Off-Peak"]?.["Peak"]?.behindMeter ?? 0;
  const peakBehindMeterGrid =
    timedTariffsByTariffRate["Peak/Off-Peak"]?.["Peak"]?.behindMeterGrid ?? 0;
  const peakNetworkImport =
    timedTariffsByTariffRate["Peak/Off-Peak"]?.["Peak"]?.networkImport ?? 0;
  const peakGrid =
    timedTariffsByTariffRate["Peak/Off-Peak"]?.["Peak"]?.grid ?? 0;
  const peakTotal =
    peakBehindMeter + peakNetworkImport + peakGrid + peakBehindMeterGrid;
  const peakInRangePerc =
    peakTotal > 0
      ? (peakBehindMeter + peakBehindMeterGrid + peakNetworkImport) / peakTotal
      : 0;
  const daylightInRangePerc =
    site.energyFlowAnnual.daylightBehindMeter !== undefined &&
    site.energyFlowAnnual.daylightInRange !== undefined &&
    site.energyFlowAnnual.daylightDemand !== undefined &&
    site.energyFlowAnnual.daylightDemand > 0
      ? (site.energyFlowAnnual.daylightBehindMeter +
          site.energyFlowAnnual.daylightInRange) /
        site.energyFlowAnnual.daylightDemand
      : undefined;

  const marketTariff = (
    (site.tariffConfig.supplier.peakOffPeak.overrideRatesMap["peak"].tariff ||
      0) * 100
  ).toFixed(2);

  const generateBuyEnergyBlock = () => {
    return siteCanBuy(organisation, site) &&
      (isGeneratingSite ||
        isOwnerOccupier ||
        organisation.orgType === "Tenant") ? (
      <Column
        style={{ width: "90%" }}
        $margin={"0 15px 15px 15px"}
        $padding={"0 8px"}
        $justifyContent={"flex-end"}
      >
        <div
          style={{
            fontWeight: "500",
            fontSize: "14px",
            margin: "auto 0px",
          }}
        >
          {fetchSiteSdmList.isLoading && (
            <span>Finding a Marketplace buy offer…</span>
          )}
          {!fetchSiteSdmList.isLoading && (
            <>
              {formatBuyDescription(
                organisation,
                site,
                investmentModel,
                bestBuyOffer!,
                false,
                true
              )}
            </>
          )}
        </div>
        <SimpleButton
          fontWeight="400"
          width={"35%"}
          label={`Buy ${isNotYetProcuringSite ? "" : "more"} energy`}
          onClick={() => {
            trackUserInteraction(
              {
                organisation_name: organisation.name,
                site_id: site.id,
                site_name: site.name,
                site_operational_status: site.operationalStatus,
                label: `Buy ${isNotYetProcuringSite ? "" : "more"} energy`,
                button_location: "site metrics tile",
              },
              "ENERGY_OFFER",
              "ENERGY_OFFER_SITE_BUTTON_CLICK",
              user!.email.toLowerCase(),
              "customer-app"
            );
          }}
          to={`/org/${organisation.id}/marketplace?siteId=${site.id}&offerType=buy`}
          fontColor={"white"}
          background={"#00022F"}
        />
      </Column>
    ) : undefined;
  };

  const showTenantNamesInHeader =
    organisation.orgType === "Landlord" &&
    site.siteOwnerships
      .filter((so) => !so.isTest)
      .some((so) => so.ownership === "tenant");
  const tenantNames = site.siteOwnerships
    .filter((so) => !so.isTest)
    .filter((so) => so.ownership === "tenant")
    .map((so) => so.name!)
    .sort();

  const onsiteIndexing =
    site.tariffConfig.onsitePPA.ppaType === "indexed"
      ? ` indexed ${(site.tariffConfig.onsitePPA.ppaIndex * 100).toFixed(1)}%/yr`
      : undefined;

  return (
    <>
      <GridContainer>
        {!site.tariffConfig.takeOrPay && (
          <SavingsMetric>
            <MetricTooltip>
              {showTenantNamesInHeader && (
                <h3>
                  {tenantNames[0]}
                  {"'"}
                  {tenantNames[0].toLowerCase().endsWith("s") ? "" : "s"}{" "}
                  {tenantNames.length > 1 && (
                    <Tooltip text={tenantNames.slice(1).join(", ")}>
                      <span>
                        and{" "}
                        <span
                          style={{
                            textDecoration: "dotted underline 2px",
                          }}
                        >
                          {tenantNames.length - 1} others{"'"}
                        </span>
                        &nbsp;
                      </span>
                    </Tooltip>
                  )}
                  savings with InRange
                </h3>
              )}
              {!showTenantNamesInHeader && (
                <h3>
                  Savings with InRange
                  {proposalType !== "tenant" && (
                    <> with {capitalise(site.activeInvestmentModel)} model</>
                  )}
                </h3>
              )}
              <Tooltip
                text={`The potential savings from the energy procured from InRange over the lifetime of the PPA (${ppaTotalLength} years). Considers current market tariffs, energy inflation, and any relevant passthrough charges.`}
              />
            </MetricTooltip>
            <h5>
              Projected savings from{" "}
              {formatUnits(site.totalInstalledCapacity, "kWp", 1)} PV
              {site.totalBatterySummary
                ? `, ${formatBatterySummary(site.totalBatterySummary)} battery`
                : ""}
              {site.sdmMatches.filter((m) => m.buyerId == site.id).length > 0
                ? `, and the InRange network`
                : ``}
              , given a starting market tariff of {marketTariff}{" "}
              {fractionalCurrencySymbol(site.currencyCode)}/kWh
              {site.tariffConfig.supplier.peakOffPeak.baseRate.tariff && (
                <>
                  {" "}
                  peak and{" "}
                  {(
                    site.tariffConfig.supplier.peakOffPeak.baseRate.tariff * 100
                  ).toFixed(2)}{" "}
                  {fractionalCurrencySymbol(site.currencyCode)}/kWh off-peak
                </>
              )}
              .
            </h5>
            <MetricRow>
              <Metric>
                <h4>
                  {formatCurrencyToNearestTen(
                    ppaSavings1,
                    site.currencyCode,
                    0,
                    false
                  )}
                </h4>
                <h5>
                  Savings over PPA
                  {ppas.length > 1 ? " 1" : ""} ({ppas[0].ppaLength} years)
                </h5>
              </Metric>
            </MetricRow>
          </SavingsMetric>
        )}
        <TariffMetric>
          <MetricTooltip>
            <h3>InRange tariff</h3>
            <Tooltip
              text={
                "InRange offers fixed and variable energy tariffs for a specified number of years via PPA. On-site tariffs are for energy delivered from an on-site PV system. InRange network tariffs are the commodity costs for buying energy from InRange via a sleeved PPA with your supplier."
              }
            />
          </MetricTooltip>

          <h5>Cost of energy procured from InRange</h5>
          <MetricRow>
            {(isGeneratingSite || hasWiredBuy) && (
              <Metric>
                <h4>{onsiteTariff}</h4>
                {onsiteTariffFreq}
                {!isGeneratingSite && hasWiredBuy ? (
                  "Wired"
                ) : onsiteIndexing ? (
                  <>
                    <h5>On-site</h5>
                    <h5>{onsiteIndexing}</h5>
                  </>
                ) : (
                  <h5>On-site</h5>
                )}
              </Metric>
            )}
            {!isOnsitePvLease && (
              <Metric>
                <h4>
                  {(site.tariffConfig.networkImportTariff * 100.0).toFixed(2) +
                    fractionalCurrencySymbol(site.currencyCode) +
                    "/kWh"}
                </h4>
                <h5>InRange network</h5>
                <h5>excl. non-commodity costs</h5>
              </Metric>
            )}
          </MetricRow>
        </TariffMetric>
        <ColumnNoDefaults style={{ height: "100%" }}>
          <NetworkSitesBar
            site={site}
            organisation={organisation}
            setShowEnergyCurvesModal={setShowEnergyCurvesModal}
            allowShowSellBar={false}
            margin={"-8px 12px 8px 12px"}
          />
          <YearDayEnergyChart
            header={"Amount of energy procured"}
            chartData={energyProcuredChartData}
            isNotYetProcuringSite={
              proposalType === "landlord" ? false : isNotYetProcuringSite
            }
            buyEnergyBlock={generateBuyEnergyBlock()}
            energyProcuredChartPeriod={energyProcuredChartPeriod}
            setEnergyProcuredChartPeriod={setEnergyProcuredChartPeriod}
            height="420px"
            flexGrow="1"
          />
        </ColumnNoDefaults>

        <EnergyDemandSidebar
          behindMeter={site.energyFlowAnnual.behindMeter}
          inrangeNetwork={site.energyFlowAnnual.networkImport}
          grid={site.energyFlowAnnual.gridImport}
          isGeneratingSite={isGeneratingSite}
          isBatterySite={isBatterySite}
          isBatteryArbitrageSite={isBatteryArbitrageSite}
        />

        <ColumnNoDefaults style={{ flexGrow: 1 }}>
          <PeakOffPeakChart
            chartData={peakOffPeakChartData}
            energyFlowAnnual={site.energyFlowAnnual}
            isGeneratingSite={isGeneratingSite}
            isBatteryArbitrageSite={isBatteryArbitrageSite}
            height="730px"
          />
        </ColumnNoDefaults>

        <EnergySupplySidebar
          isGeneratingSite={isGeneratingSite}
          isBatterySite={isBatterySite}
          isBatteryArbitrageSite={isBatteryArbitrageSite}
          peakInRange={peakInRangePerc}
          daylightInRange={daylightInRangePerc}
          peakHoursStartIndex={
            site.tariffConfig.supplier.peakOffPeak.overrideRatesMap["peak"]
              .rangesMap["0"].startIndex
          }
          peakHoursEndIndex={
            site.tariffConfig.supplier.peakOffPeak.overrideRatesMap["peak"]
              .rangesMap["0"].endIndex
          }
        />
      </GridContainer>
      {showEnergyCurvesModal && (
        <MarketplaceEnergyCurvesModal
          organisation={organisation}
          site={site}
          offerType={
            siteCanSell(organisation, site, investmentModel) ? "sell" : "buy"
          }
          setShowModal={setShowEnergyCurvesModal}
          urlSpillSiteId={urlSpillSiteId}
        />
      )}
    </>
  );
};

export default EnergySavings;

const GridContainer = styled.div`
  margin: 16px;
  display: grid;
  grid-template-columns: auto 470px;
  grid-template-rows: auto auto auto;
  gap: 16px;
  width: calc(100% - 32px);
`;

const MetricTooltip = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SavingsMetric = styled.div`
  padding: 16px 16px;
  border-radius: 8px;
  flex-grow: 1;
  background: #eee;
  height: 164px;

  h3 {
    font-size: 20px;
    font-weight: 600;
    line-height: 20px;
    padding: 0;
    margin: 0 0 8px;
  }
  h4 {
    font-size: 24px;
    font-weight: 600;
    line-height: 25px;
    padding: 0;
    margin: 8px 0;
  }
  h5 {
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    padding: 0;
    margin: 0;
  }
`;

const TariffMetric = styled(SavingsMetric)`
  flex-grow: 0;
`;

const Metric = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
`;
const MetricRow = styled(Row)`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
`;
